import React, {useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {constructPath, createPathStringFromArray, findGroup} from '../../helpers/helperfunctions.js';
import {useDispatch, useSelector} from 'react-redux';
import DeviceOverviewTableBody from '../../components/overview/overviewtable/tbody/DeviceOverviewTableBody.js';
import TFooter from '../../components/shared/tfooter/TFooter.js';
import {setOverviewFilter} from "../../helpers/reduxstore/reducers/filterReducer.js";
import useSetLocation from "../../helpers/hooks/useSetLocation.js";
import {getCurrentLanguage} from "../../helpers/i18nConfig.js";
import {queryDeviceList, queryDeviceAggregates} from '../../api/deviceList.js';
import Button from "react-bootstrap/Button";
import {
    CheckCircle,
    ExclamationTriangle,
    Image,
    LightningCharge,
    ListUl
} from "react-bootstrap-icons";
import FilterModal from "../../components/shared/filterModal/FilterModal.js";
import FilterButton from "../../components/shared/filterbutton/FilterButton.js";
import {SearchInput} from "../../components/shared/searchinput/SearchInput.js";

/** Renders the device overview list */
export default function DeviceOverviewPage() {
    useSetLocation("Airlinq Online")
    const {t} = useTranslation(['overviewpage', 'common', 'telemetry'])
    const dispatch = useDispatch()

    const {overviewFilter} = useSelector(state => state.filter)
    const {allowedGroups} = useSelector(state => state.group)
    const [filterModalVisibility, setFilterModalVisibility] = useState(false)

    const [search, setSearch] = useState(overviewFilter.searchString ?? '')
    const timeoutHandle = useRef(null)

    // when typing in search input, wait some time before sending to backend, so we don't spam too much
    function startSearchTimeout(searchValue) {
        setSearch(searchValue)
        if (timeoutHandle.current !== null) window.clearTimeout(timeoutHandle.current)
        timeoutHandle.current = setTimeout(() => {
            setSearch(searchValue)
            dispatch(setOverviewFilter({searchString: searchValue}))
        }, 1000)
    }

    const [deviceListLoading, setDeviceListLoading] = useState(true);
    const [aggregatesLoading, setAggregatesLoading] = useState(true);
    const [firstLoad, setFirstLoad] = useState(true);
    const [shownDeviceList, setShownDeviceList] = useState([]);
    const [deviceListCount, setDeviceListCount] = useState(0);
    const [deviceList, setDeviceList] = useState([]);
    const [aggregates, setAggregates] = useState({
        alarm: '-', warning: '-', ok: '-', disconnected: '-'
    });
    const deviceListAbortControllerRef = useRef(new AbortController());
    const aggregatesAbortControllerRef = useRef(new AbortController());

    function createOverviewFilter({page, rowsPerPage, searchString, deviceListConditionFilter, filterGroups, deviceListSort}) {
        const sanitizedSearchString = searchString?.replaceAll("'", "").replaceAll('"', '');

        return {
            skip: (page - 1) * rowsPerPage,
            take: rowsPerPage,
            state: Object.keys(deviceListConditionFilter)
                .filter(key => deviceListConditionFilter[key])
                .join(","),
            search: encodeURIComponent(sanitizedSearchString?.trim() ?? ''),
            groups: filterGroups.map(g => g.id).join(","),
            sort_direction: deviceListSort?.sortDirection ?? "asc",
            sort_value: deviceListSort?.sortValue ?? "name",
            lang: getCurrentLanguage()
        };
    }


    useEffect(() => {
        setDeviceListLoading(true);
        if (deviceListAbortControllerRef.current && deviceListAbortControllerRef.current.signal)
            deviceListAbortControllerRef.current.abort("Query renewed");
        deviceListAbortControllerRef.current = new AbortController();
        const filter = createOverviewFilter(overviewFilter);

        queryDeviceList(filter, deviceListAbortControllerRef.current.signal)
            .then(res => {
                const rawList = Array.isArray(res)
                    ? res
                    : (res?.deviceList || res?.list || []);
                const listWithPath = rawList.map(deviceWithPath);
                setDeviceList(listWithPath);
                setDeviceListLoading(false);
                if (firstLoad) setFirstLoad(false);
            })
            .catch(e => {
                if (e.message.includes("The user aborted a request") || e.message.includes("Query renewed")) {
                    console.warn("Request aborted or renewed: ", e.message);
                } else {
                    setDeviceListLoading(false);
                    console.error("Error fetching device list: ", e);
                }
            });
    }, [overviewFilter.page, overviewFilter.rowsPerPage, overviewFilter.searchString, overviewFilter.filterGroups, overviewFilter.deviceListConditionFilter, overviewFilter.deviceListSort]);

    useEffect(() => {
        setAggregatesLoading(true);
        if (aggregatesAbortControllerRef.current && aggregatesAbortControllerRef.current.signal)
            aggregatesAbortControllerRef.current.abort("Query renewed");
        aggregatesAbortControllerRef.current = new AbortController();
        const filter = createOverviewFilter(overviewFilter);

        queryDeviceAggregates(filter, aggregatesAbortControllerRef.current.signal)
            .then(res => {
                setAggregates({
                    alarm: res?.alarm ?? '-',
                    warning: res?.warning ?? '-',
                    ok: res?.ok ?? '-',
                    disconnected: res?.disconnected ?? '-'
                });
                setDeviceListCount(res?.count ?? 0);
                setAggregatesLoading(false);
            })
            .catch(e => {
                if (e.message.includes("The user aborted a request") || e.message.includes("Query renewed")) {
                    console.warn("Aggregates request aborted or renewed: ", e.message);
                } else {
                    setAggregatesLoading(false);
                    console.error("Error fetching aggregates: ", e);
                }
            });
    }, [overviewFilter.searchString, overviewFilter.filterGroups, overviewFilter.deviceListConditionFilter, overviewFilter.deviceListSort]);

    useEffect(() => {
        if (!deviceList) return;
        setShownDeviceList(deviceList);
    }, [deviceList]);

    function deviceWithPath(device) {
        return {
            ...device,
            path: createPathStringFromArray(constructPath(findGroup(device.parent, allowedGroups), allowedGroups), device.name)
        }
    }

    /** @param {int} rowsPerPage */
    const dispatchSetOverviewRowsPerPage = (rowsPerPage) => {
        dispatch(setOverviewFilter({rowsPerPage: rowsPerPage, page: 1}))
    }

    /** @param {int} page */
    function setPage(page) {
        dispatch(setOverviewFilter({page}))
    }

    function flipConditionFilter(name) {
        dispatch(setOverviewFilter({
            deviceListConditionFilter: {
                ...overviewFilter.deviceListConditionFilter,
                [name]: !overviewFilter.deviceListConditionFilter[name]
            },
            page: 1
        }));
    }

    return (
        <div className="d-flex flex-column" style={{flex: '1'}}>
            <FilterModal selectedItemsList={overviewFilter.filterGroups} show={filterModalVisibility}
                         dismiss={() => setFilterModalVisibility(false)}
                         submit={chosenGroupList => {
                             dispatch(setOverviewFilter({filterGroups: chosenGroupList, page: 1}));
                             setFilterModalVisibility(false)
                         }}
                         modalTitle={t('overviewpage:filter_modal.title')}
                         modalBody={t('overviewpage:filter_modal.body')}
                         dropzoneBody={t('overviewpage:filter_modal.dropzone_body')}
            />

            {/* === Title and search === */}
            <div className='d-md-flex justify-content-between mt-md-4 mb-md-3 mt-xs-2 mb-xs-2'>
                <div className='mb-xs-2' style={{flex: 2}}>
                    <div className='title-font'
                         data-testid='title'>{t('overviewpage:info_section.info_section_title')}</div>
                    {/*<span style={{fontSize: '1.2rem'}}>{t('overviewpage:info_section.info_section_p')}</span>*/}
                </div>
                <div className='d-flex align-items-end' style={{width: '324px'}}>
                    <SearchInput disabled={firstLoad} setSearchString={e => startSearchTimeout(e.target.value)} searchString={search}
                                 placeholder={t('common:search.placeholder')}/>
                </div>
            </div>

            {/* === Filters === */}
            <div className='d-md-flex align-items-center justify-content-between mb-3'>
                <div className='d-flex align-items-center mb-xs-2'>
                    <Button  disabled={firstLoad} className='me-2' variant='outline-secondary'
                            onClick={() => dispatch(setOverviewFilter({listViewChosen: !overviewFilter.listViewChosen}))}>
                        {overviewFilter.listViewChosen ? <Image/> : <ListUl/>}
                        <span
                            className='ms-2'>{overviewFilter.listViewChosen ? t('overviewpage:viewmode.large') : t('overviewpage:viewmode.small')}</span>
                    </Button>

                    <FilterButton disabled={firstLoad} showModal={() => setFilterModalVisibility(true)}
                                  removeFilter={() => dispatch(setOverviewFilter({filterGroups: [], page: 1}))}
                                  hasPopOver={true} filterGroups={overviewFilter.filterGroups}/>
                    
                    <div className='ms-3'>
                        {t('overviewpage:table.headers.total')}: {aggregatesLoading ? (
                            <div className="spinner-border spinner-border-lg text-primary" style={{height: '15px', width: '15px'}}></div>
                        ) : (
                            (() => {
                                // Summer kun de aktive statusser
                                let total = 0;
                                if (overviewFilter.deviceListConditionFilter.ok) total += Number(aggregates.ok) || 0;
                                if (overviewFilter.deviceListConditionFilter.warning) total += Number(aggregates.warning) || 0;
                                if (overviewFilter.deviceListConditionFilter.alarm) total += Number(aggregates.alarm) || 0;
                                if (overviewFilter.deviceListConditionFilter.disconnected) total += Number(aggregates.disconnected) || 0;
                                // Hvis ingen status er valgt, vises summen af alle
                                if (!overviewFilter.deviceListConditionFilter.ok && !overviewFilter.deviceListConditionFilter.warning && !overviewFilter.deviceListConditionFilter.alarm && !overviewFilter.deviceListConditionFilter.disconnected) {
                                    total = (Number(aggregates.ok) || 0) + (Number(aggregates.warning) || 0) + (Number(aggregates.alarm) || 0) + (Number(aggregates.disconnected) || 0);
                                }
                                return total;
                            })()
                        )}
                    </div>
                </div>

                <div className='d-flex align-items-center flex-wrap'>
                    <Button
                        className={`d-flex align-items-center justify-content-center me-1 mt-xs-2`}
                        variant='outline-success' style={{minWidth: '160px'}}
                        active={overviewFilter.deviceListConditionFilter.ok}
                        onClick={() => flipConditionFilter('ok')}>
                            <CheckCircle/> <span className='ms-1'>
                                {aggregatesLoading ? <div className="spinner-border spinner-border-lg text-primary" style={{height: '15px', width: '15px'}}></div> : aggregates.ok ?? 0} {t('overviewpage:table.status.ok')}
                            </span>
                    </Button>
                    <Button
                        className={`d-flex align-items-center justify-content-center me-1`}
                        variant='outline-warning' style={{minWidth: '160px'}}
                        active={overviewFilter.deviceListConditionFilter.warning}
                        onClick={() => flipConditionFilter('warning')}>
                            <ExclamationTriangle/> <span className='ms-1'>
                                {aggregatesLoading ?<div className="spinner-border spinner-border-lg text-primary" style={{height: '15px', width: '15px'}}></div> : aggregates.warning ?? 0} {t('overviewpage:table.status.warning')}
                            </span>
                    </Button>
                    <Button
                        className={`d-flex align-items-center justify-content-center me-1`}
                        variant='outline-danger' style={{minWidth: '160px'}}
                        active={overviewFilter.deviceListConditionFilter.alarm}
                        onClick={() => flipConditionFilter('alarm')}>
                            <ExclamationTriangle/> <span className='ms-1'>
                                {aggregatesLoading ? <div className="spinner-border spinner-border-lg text-primary" style={{height: '15px', width: '15px'}}></div> : aggregates.alarm ?? 0} {t('overviewpage:table.status.alarm')}
                            </span>
                    </Button>
                    <Button 
                        className='d-flex align-items-center justify-content-center' variant='outline-dark'
                        style={{minWidth: '160px'}}
                        active={overviewFilter.deviceListConditionFilter.disconnected}
                        onClick={() => flipConditionFilter('disconnected')}>
                            <LightningCharge/> <span className='ms-1'>
                                {aggregatesLoading ? <div className="spinner-border spinner-border-lg text-primary" style={{height: '15px', width: '15px'}}></div> : aggregates.disconnected ?? 0} {t('common:connectionstate.false')}
                            </span>
                    </Button>   
                </div>
            </div>

            {/* === Table === */}
            <DeviceOverviewTableBody
                className={'mb-2'}
                shownDeviceList={shownDeviceList}
                listViewChosen={overviewFilter.listViewChosen}
                deviceListLoading={deviceListLoading}
                deviceListSort={overviewFilter.deviceListSort}
                tempType={overviewFilter.showTemperatureType}
            />

            <TFooter
                className="mb-3"
                setRowsPerPage={dispatchSetOverviewRowsPerPage}
                rowsPerPage={overviewFilter.rowsPerPage}
                dataLength={deviceListCount ?? 0}
                setPage={setPage}
                page={overviewFilter.page}
            />

        </div>
    )
}